// ─────────────────────────────────────────────────────────────────────────────
// Artist CNTRL, Capacitor access helpers (web React, NOT React Native).
//
// The app is a web app wrapped in a WKWebView by Capacitor. These helpers mirror
// the legacy m-app.js plumbing (_capPlugin / _isPluginAvailable) so the React
// mobile screens can reach native plugins WITHOUT importing @capacitor/* (which
// the web/Vite build doesn't bundle). On the web (no Capacitor) every helper
// degrades to null/false so the same code runs unchanged in the browser.
//
// Capacitor injects window.Capacitor at runtime in the native app. We never call
// a plugin proxy unless isPluginAvailable() says it's actually installed, an
// un-installed proxy rejects with UNIMPLEMENTED and spams the console.
// ─────────────────────────────────────────────────────────────────────────────

/* eslint-disable @typescript-eslint/no-explicit-any */

interface CapacitorGlobal {
  Plugins?: Record<string, any>;
  isNativePlatform?: () => boolean;
  isPluginAvailable?: (name: string) => boolean;
  getPlatform?: () => string;
  addListener?: (...args: any[]) => any;
}

function cap(): CapacitorGlobal | null {
  try {
    const w = window as unknown as { Capacitor?: CapacitorGlobal };
    return w.Capacitor || null;
  } catch {
    return null;
  }
}

/** True only inside the native Capacitor app (iOS/Android), false on the web. */
export function isNativePlatform(): boolean {
  try {
    const c = cap();
    return !!(c && typeof c.isNativePlatform === 'function' && c.isNativePlatform());
  } catch {
    return false;
  }
}

/** 'ios' | 'android' | 'web'. */
export function getPlatform(): string {
  try {
    const c = cap();
    return (c && typeof c.getPlatform === 'function' && c.getPlatform()) || 'web';
  } catch {
    return 'web';
  }
}

/** Whether a named plugin is actually registered natively (avoids UNIMPLEMENTED). */
export function isPluginAvailable(name: string): boolean {
  try {
    const c = cap();
    return !!(c && typeof c.isPluginAvailable === 'function' && c.isPluginAvailable(name));
  } catch {
    return false;
  }
}

/** Return the plugin object ONLY if it's installed natively, else null. */
export function getPlugin<T = any>(name: string): T | null {
  const c = cap();
  if (!c || !c.Plugins) return null;
  if (!isPluginAvailable(name)) return null;
  return (c.Plugins[name] as T) || null;
}
